"use client";

import Link from 'next/link';
import { ThemeToggle } from '@/components/ThemeToggle';

export default function PrivacyPage() {
	return (
		<main className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
			<header className="w-full p-6 flex justify-between items-center">
				<Link href="/" className="text-2xl font-bold text-gradient bg-gradient-to-r from-primary-600 via-teal-500 to-primary-600 bg-clip-text text-transparent">
					Karrelo
				</Link>
				<ThemeToggle />
			</header>
			
			<div className="max-w-4xl mx-auto px-6 py-12">
				<div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-700 dark:text-slate-300">
					<h1 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900 dark:text-slate-100">Integritetspolicy för Karrelo</h1>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">1. Hur vi hanterar dina personuppgifter</h2>
						<p>
							Karrelo behandlar personuppgifter i enlighet med GDPR.
						</p>
						<p>
							Vi samlar bara in den information som behövs för att skapa ditt konto och kunna erbjuda dig våra funktioner – som CV-analys, jobbmatchning och stöd att skapa professionella ansökningshandlingar.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">2. Vilka uppgifter vi samlar in</h2>
						<p>Vi hanterar följande kategorier av uppgifter:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Kontaktuppgifter (namn, e-post)</li>
							<li>CV och ansökningsdokument</li>
							<li>Utbildning, arbetslivserfarenhet, kompetenser och språk</li>
							<li>Preferenser för jobb och plats</li>
							<li>Användningsdata i systemet (t.ex. vilka funktioner som används)</li>
						</ul>
						<p className="mt-3">
							Karrelo begär inte känsliga personuppgifter, men om du själv laddar upp sådant i ditt CV behandlas det enbart för tjänstens funktioner.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">3. Varför vi behandlar uppgifterna</h2>
						<p>Vi använder dina uppgifter för att:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Skapa och administrera ditt konto</li>
							<li>Generera CV, personligt brev och analyser</li>
							<li>Matcha din profil mot relevanta jobb</li>
							<li>Förbättra tjänstens funktioner</li>
							<li>Skicka viktiga meddelanden kopplade till din användning</li>
						</ul>
						<p className="mt-3 font-medium">
							Vi säljer aldrig personuppgifter.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">4. Delning av uppgifter</h2>
						<p>Vi delar enbart data med:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Tekniska leverantörer som driftar tjänsten och skickar verifierings-/supportmail</li>
							<li>AI-leverantörer som används för att bearbeta text, under förutsättning att de följer GDPR</li>
							<li>Arbetsgivare, men endast när du själv godkänner det</li>
						</ul>
						<p className="mt-3">
							Samtliga underleverantörer har bindande personuppgiftsbiträdesavtal (DPA).
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">5. Hur länge vi sparar uppgifterna</h2>
						<p>
							Dina uppgifter sparas så länge du har ett konto hos oss.
						</p>
						<p>
							Om du raderar ditt konto raderas dina dokument och personuppgifter inom 30 dagar.
						</p>
						<p>
							Anonymiserad statistik sparas längre, men kan inte kopplas till dig.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">6. Dina rättigheter enligt GDPR</h2>
						<p>Du har rätt att:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Få tillgång till dina personuppgifter</li>
							<li>Begära rättning eller radering</li>
							<li>Invända mot behandling</li>
							<li>Begära dataportabilitet</li>
							<li>Återkalla samtycke</li>
							<li>Lämna klagomål till Integritetsskyddsmyndigheten (IMY)</li>
						</ul>
						<p className="mt-3">
							Kontakta oss på <a href="mailto:support@karrelo.se" className="text-primary-600 dark:text-primary-400 hover:underline">support@karrelo.se</a> för att utöva dina rättigheter.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
						<p>
							Karrelo använder endast nödvändiga cookies för inloggning och funktionalitet.
						</p>
						<p>
							Vi använder inte tredjepartsspårning eller annonseringscookies.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">8. Säkerhet</h2>
						<p>Vi skyddar personuppgifter med:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Kryptering (HTTPS)</li>
							<li>Hashade lösenord</li>
							<li>Åtkomstkontroller</li>
							<li>Loggning av säkerhetsrelaterade händelser</li>
						</ul>
						<p className="mt-3">
							Vi arbetar löpande med att förbättra säkerheten i tjänsten.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">9. Kontakt</h2>
						<p>
							<strong>Karrelo - En del av Jobb Store i Sverige AB (559046-0282)</strong>
						</p>
						<p>
							E-post: <a href="mailto:support@karrelo.se" className="text-primary-600 dark:text-primary-400 hover:underline">support@karrelo.se</a>
						</p>
						<p>
							Stockholm, Sverige
						</p>
						<p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
							Senast uppdaterad: 1 september 2025
						</p>
					</section>
				</div>
				
				<div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
					<Link href="/" className="text-primary-600 dark:text-primary-400 hover:underline">
						← Tillbaka till startsidan
					</Link>
				</div>
			</div>
		</main>
	);
}
